import React, { useEffect, useMemo } from 'react';
import { ArrowDownNav as ArrowDown, Bullet, External } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { iconSize } from '@fabric-ui/utils';
import { Box, Collapse, ListItemButton, ListItemIcon, ListItemText, listItemTextClasses, useTheme } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { useAvContext } from '../../context/AvContextProvider';
import { SubMenuItem } from '../../types/sideBar.types';
import { useLocation } from '../../utils/AvRouter';

const RouterLink = Link as any;
const ArrowDownIcon = isCollapse => (
  <ArrowDown style={{ ...iconSize(16), transform: `rotate(${isCollapse ? 180 : 0}deg)` }} className="showOnHover" />
);

interface Props extends SubMenuItem {
  isOpen: boolean;
  openedSubMenus?: any[];
  setOpenedSubMenus: React.Dispatch<React.SetStateAction<any[]>>;
  ffForDisplaying?: string;
  ffForHiding?: string;
}

const SideBarItem: React.FC<Props> = ({
  path,
  name,
  icon,
  isOpen,
  subMenu,
  openedSubMenus = [],
  setOpenedSubMenus,
  fixedTabHeight,
  alwaysShowSubMenu = false,
  flatSubMenuPaths = false,
}) => {
  const location = useLocation();
  const theme = useTheme();
  const { getPathName, selectedApp, accountId } = useAvContext();
  const pathname = getPathName(path).to;
  const isSubMenuExpanded = useMemo(
    () => openedSubMenus.some(v => v.name === name && v.app === selectedApp),
    [openedSubMenus, name, selectedApp]
  );

  const isPathActive = (basePath: string) => {
    const currentPath = location.pathname.toLowerCase();
    const lowerCaseBasePath = basePath.toLowerCase();
    return currentPath === lowerCaseBasePath || currentPath.startsWith(`${lowerCaseBasePath}/`);
  };

  const childPath = (subItem: SubMenuItem) => (flatSubMenuPaths ? subItem.path : `${path}/${subItem.path}`);
  const childActivePath = (subItem: SubMenuItem) => (flatSubMenuPaths ? getPathName(subItem.path).to : `${pathname}/${subItem.path}`);

  const isSomeSubMenuSelected = useMemo(
    () => subMenu?.some(subItem => isPathActive(childActivePath(subItem))),
    [subMenu, location.pathname, pathname, flatSubMenuPaths]
  );
  const handleChangeSubMenuExpanded = () =>
    setOpenedSubMenus(
      openedSubMenus.some(v => v.name === name)
        ? openedSubMenus.filter(v => v.name !== name)
        : [...openedSubMenus, { name, app: selectedApp }]
    );

  useEffect(() => {
    if (location.pathname.includes(`/${path}/`)) {
      setOpenedSubMenus([{ name, app: selectedApp }]);
    }
  }, [location.pathname]);

  return subMenu ? (
    subMenu.length > 1 || (alwaysShowSubMenu && subMenu.length === 1) ? (
      <Box sx={{ ...subMenuContainer(theme, isSubMenuExpanded, isSomeSubMenuSelected) }}>
        <ListItemButton
          sx={{ ...subMenuParent(theme, isOpen, fixedTabHeight) }}
          onClick={handleChangeSubMenuExpanded}
          selected={isSomeSubMenuSelected}>
          <ListItemIcon sx={listItemIconStyle}>{icon}</ListItemIcon>
          <Collapse in={isOpen} orientation="horizontal" timeout={theme.transitions.duration.complex} sx={{ flexGrow: 1 }}>
            <ListItemText>{name}</ListItemText>
          </Collapse>
          {isOpen && ArrowDownIcon(isSubMenuExpanded)}
        </ListItemButton>
        <Collapse in={isSubMenuExpanded} orientation="vertical" timeout={theme.transitions.duration.standard}>
          <Box sx={{ gap: 1, py: 1, ...(isOpen ? { ...flex.col } : { ...flex.colCenter }) }}>
            {subMenu.map(subItem =>
              isOpen ? (
                <ListItemButton
                  sx={{
                    ...item(theme, subItem.fixedTabHeight),
                    ...flex.itemsCenter,
                    pl: '18px',
                    ':hover': {
                      backgroundColor: isSomeSubMenuSelected
                        ? 'var(--color-surface-list-selectedHover)'
                        : 'var(--color-surface-accent-blueGrey-secondary)',
                    },
                    '&.Mui-selected, &.Mui-selected:hover': {
                      backgroundColor: 'var(--color-surface-list-selectedHover)',
                    },
                  }}
                  selected={isPathActive(childActivePath(subItem))}
                  component={RouterLink}
                  key={subItem.path}
                  {...(subItem.externalLink && { target: '_blank', from: '/' })}
                  to={subItem.externalLink || getPathName(childPath(subItem)).to}
                  params={{ accountId }}>
                  <Collapse in={isOpen} orientation="horizontal" timeout={theme.transitions.duration.short}>
                    <ListItemText>{subItem.name}</ListItemText>
                  </Collapse>
                  {subItem.externalLink && (
                    <ListItemIcon sx={listItemIconStyle}>
                      <External />
                    </ListItemIcon>
                  )}
                </ListItemButton>
              ) : (
                <ListItemButton
                  sx={{ ...item(theme, subItem.fixedTabHeight), ...flex.itemsCenter }}
                  key={subItem.path}
                  selected={isPathActive(childActivePath(subItem))}>
                  <Bullet />
                </ListItemButton>
              )
            )}
          </Box>
        </Collapse>
      </Box>
    ) : subMenu.length === 1 ? (
      <ListItemButton
        sx={item(theme, fixedTabHeight)}
        selected={isPathActive(childActivePath(subMenu[0]))}
        component={RouterLink}
        to={getPathName(childPath(subMenu[0])).to}
        params={{ accountId }}>
        <ListItemIcon sx={listItemIconStyle}>{icon}</ListItemIcon>
        <Collapse in={isOpen} orientation="horizontal" timeout={theme.transitions.duration.short}>
          <ListItemText>{subMenu[0].name}</ListItemText>
        </Collapse>
      </ListItemButton>
    ) : null
  ) : (
    <ListItemButton
      sx={item(theme, fixedTabHeight)}
      selected={isPathActive(pathname)}
      component={RouterLink}
      to={pathname}
      onClick={() => {
        setOpenedSubMenus([]);
      }}
      params={{ accountId }}>
      <ListItemIcon sx={listItemIconStyle}>{icon}</ListItemIcon>
      <Collapse in={isOpen} orientation="horizontal" timeout={theme.transitions.duration.short}>
        <ListItemText>{name}</ListItemText>
      </Collapse>
    </ListItemButton>
  );
};
export default SideBarItem;

const listItemIconStyle = {
  minWidth: 'inherit',
  height: 20,
  width: 20,
  svg: {
    height: 'inherit',
    width: 'inherit',
  },
};

export const item = (theme, fixedTabHeight?) => ({
  ...flex.row,
  height: fixedTabHeight || 32,
  width: 'auto',
  padding: '7px',
  margin: '0px 18px',
  gap: 1,
  borderRadius: '12px',
  color: 'var(--color-content-base-primary)',
  svg: {
    color: 'var(--color-content-base-secondary)',
  },
  transition: theme.transitions.create(['color', 'background-color'], {
    duration: theme.transitions.duration.shortest,
  }),
  '&:hover': {
    backgroundColor: 'var(--color-surface-nav-sideBar-hover)',
  },
  '&.Mui-selected': {
    [`.${listItemTextClasses.root} .MuiTypography-root`]: {
      fontWeight: 600,
    },
    color: 'var(--color-content-interactive-primary-default)',
    svg: {
      color: 'var(--color-content-interactive-primary-default)',
    },
    '&, &:hover': {
      backgroundColor: 'var(--color-surface-nav-sideBar-active)',
    },
  },

  [`.${listItemTextClasses.root}`]: {
    overflow: 'hidden',
    whiteSpace: fixedTabHeight ? 'normal' : 'nowrap',
  },
});

export const subMenuContainer = (theme, isSubMenuOpened, isSomeSubMenuSelected) => ({
  ...((isSubMenuOpened || isSomeSubMenuSelected) && { borderRadius: '12px' }),
  ml: 2.5,
  mr: 2,
  backgroundColor: isSomeSubMenuSelected
    ? 'var(--color-surface-nav-sideBar-active)'
    : isSubMenuOpened
      ? 'var(--color-surface-nav-sideBar-hover)'
      : undefined,
});

export const subMenuParent = (theme, isOpen, fixedTabHeight = 32) => ({
  ...flex.row,
  height: fixedTabHeight,
  borderRadius: '12px',
  margin: '0px 0px',
  padding: '7px',
  width: isOpen ? '100%' : '20px',
  color: 'var(--color-content-base-primary)',
  svg: {
    color: 'var(--color-content-base-secondary)',
  },
  '& .showOnHover': {
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover .showOnHover': {
    opacity: 1,
  },
  '&:hover': {
    backgroundColor: 'var(--color-surface-nav-sideBar-hover)',
  },
  '&.Mui-selected': {
    backgroundColor: 'var(--color-surface-nav-sideBar-active)',
    color: 'var(--color-content-interactive-primary-default)',
    svg: {
      color: 'var(--color-content-interactive-primary-default)',
    },
  },
});
